import React, { useState, useRef, useCallback, useMemo } from 'react';
import { Dropdown } from '@/components/AntdCompatDropdown';
import {
  SearchInput,
  EllipsisTooltip,
  TeamTagPill,
  CommonDrawer,
} from '@/components';
import TeamForm from '@/pages/Settings/TeamForm';
import { useDispatch } from 'react-redux';
import { fetchTeamsDDL } from '@/redux-toolkit/slice/teamsSlice';
import type { AppDispatch } from '@/redux-toolkit/store';
import useToken from '@/common/helper/useToken';
import './TeamsDropdownLB.css';
import { Checkbox, Flex, Typography } from 'antd';
import { CloseIcon, DoubleChevronDownRoleIcon } from '@/assets/icons/icons';
import type { TeamGroup } from '@/apis/teamGroups';
import {
  groupMatchesSearch,
  groupSelectionState,
  teamPickerSectionLabels,
  toggleGroupSelection,
} from '@/common/utils/teamGroups';

export interface Team {
  id: string;
  name: string;
  color?: string;
  needs_setup?: boolean;
  permissions?: {
    is_create?: boolean;
    is_update?: boolean;
  };
  disabled?: boolean;
  teams_webhook?: string | null;
  slack_webhook?: string | null;
  slack_connected?: boolean;
  general_support_email?: string | null;
}

export interface TeamsDropdownLBProps<T extends Team = Team> {
  teamsOptions: T[];
  selectedTeams: T[];
  /** Called with option objects (or kept selected ones). */
  onChange: (teams: T[]) => void;
  disabled?: boolean;
  onSearch?: (value: string) => void;
  teamsSearch?: string;
  provider?: 'teams' | 'slack' | 'email';
  /**
   * Opt-in team groups, shown above the teams. Picking one selects its
   * connected teams (teams without the provider set up can't receive the
   * message, same as picking them one by one); picking it again clears them.
   * Groups themselves are never added to the selection.
   */
  teamGroups?: TeamGroup[];
  /**
   * Unfiltered options used to resolve a group's teams while a search
   * narrows ``teamsOptions``. Defaults to ``teamsOptions``.
   */
  groupTeamsOptions?: T[];
}

const isTeamConnected = (
  team: Team,
  p: 'teams' | 'slack' | 'email'
): boolean => {
  if (p === 'slack') return Boolean(team.slack_connected);
  if (p === 'email') return Boolean(team.general_support_email);
  return Boolean(team.teams_webhook);
};

const TeamsDropdownLB = <T extends Team>({
  teamsOptions = [],
  selectedTeams = [],
  onChange,
  disabled = false,
  onSearch,
  teamsSearch = '',
  provider = 'teams',
  teamGroups,
  groupTeamsOptions,
}: TeamsDropdownLBProps<T>): React.ReactElement => {
  const [open, setOpen] = useState<boolean>(false);
  const triggerRef = useRef<HTMLDivElement>(null);
  const [dropdownWidth, setDropdownWidth] = useState<number | undefined>(
    undefined
  );

  const [setupTeam, setSetupTeam] = useState<T | null>(null);
  const [drawerOpen, setDrawerOpen] = useState<boolean>(false);
  const dispatch = useDispatch<AppDispatch>();
  const { getToken } = useToken();

  const currentSearchQuery = teamsSearch !== undefined ? teamsSearch : '';

  const handleSearchChange = useCallback(
    (val: string) => {
      onSearch?.(val);
    },
    [onSearch]
  );

  const handleClearSearch = useCallback(() => {
    onSearch?.('');
  }, [onSearch]);

  const toggleOpen = useCallback(() => {
    if (!disabled) {
      const nextOpen = !open;
      setOpen(nextOpen);
      if (nextOpen && triggerRef.current) {
        setDropdownWidth(triggerRef.current.clientWidth);
      }
      if (!nextOpen && currentSearchQuery) {
        onSearch?.('');
      }
    }
  }, [disabled, open, onSearch, currentSearchQuery]);

  const handleVisibleChange = useCallback(
    (visible: boolean) => {
      if (!disabled) {
        setOpen(visible);
        if (visible && triggerRef.current) {
          setDropdownWidth(triggerRef.current.clientWidth);
        }
        if (!visible && currentSearchQuery) {
          onSearch?.('');
        }
      }
    },
    [disabled, onSearch, currentSearchQuery]
  );

  const handleToggleSelect = (team: T, shouldSelect: boolean): void => {
    if (shouldSelect) {
      onChange([...selectedTeams, team]);
    } else {
      const isSelected = selectedTeams.some((t) => t.id === team.id);
      if (isSelected) {
        onChange(selectedTeams.filter((t) => t.id !== team.id));
      }
    }
  };

  const handleRemove = (teamId: string, e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (disabled) return;
    onChange(selectedTeams.filter((team) => team.id !== teamId));
  };

  const handleClearAll = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (disabled) return;
    onChange([]);
  };

  const filteredTeams = teamsOptions;

  // Split into Connected vs Need setup based on provider
  const connectedTeams = filteredTeams.filter((team) =>
    isTeamConnected(team, provider)
  );
  const needSetupTeams = filteredTeams.filter(
    (team) => !isTeamConnected(team, provider)
  );

  // Groups limited to their connected, enabled teams in this picker.
  const groupOptions = groupTeamsOptions ?? teamsOptions;
  const pickableGroups = useMemo(() => {
    if (!teamGroups?.length) return [];
    const pickable = new Set(
      groupOptions
        .filter((team) => !team.disabled && isTeamConnected(team, provider))
        .map((team) => String(team.id))
    );
    return teamGroups
      .map((group) => ({
        ...group,
        team_ids: group.team_ids.filter((id) => pickable.has(id)),
      }))
      .filter(
        (group) =>
          group.team_ids.length > 0 &&
          groupMatchesSearch(
            group,
            currentSearchQuery,
            (id) => groupOptions.find((team) => String(team.id) === id)?.name
          )
      );
  }, [teamGroups, groupOptions, provider, currentSearchQuery]);

  const handleGroupToggle = (group: TeamGroup): void => {
    const selectedIds = selectedTeams.map((team) => String(team.id));
    const nextIds = new Set(toggleGroupSelection(group, selectedIds));
    const kept = selectedTeams.filter((team) => nextIds.has(String(team.id)));
    const keptIds = new Set(kept.map((team) => String(team.id)));
    const added = groupOptions.filter(
      (team) => nextIds.has(String(team.id)) && !keptIds.has(String(team.id))
    );
    onChange([...kept, ...added]);
  };

  const renderGroupItem = (group: TeamGroup): React.ReactNode => {
    const state = groupSelectionState(
      group,
      selectedTeams.map((team) => team.id)
    );
    return (
      <div
        key={`team-group:${group.id}`}
        className='teams-dropdown-lb-item'
        onClick={() => handleGroupToggle(group)}
      >
        <div className='teams-dropdown-lb-item-left'>
          {/* Display only: the row handles the click. */}
          <span className='teams-dropdown-lb-group-checkbox'>
            <Checkbox
              // Still focusable, so it needs a name of its own.
              aria-label={group.name}
              className='checkbox-sm'
              checked={state.checked}
              indeterminate={state.indeterminate}
            />
          </span>
          <span
            className='teams-dropdown-lb-item-dot'
            // eslint-disable-next-line react/forbid-dom-props -- each team's own color, from data
            style={{
              backgroundColor: group.color || 'var(--gray-400)',
            }}
          />
          <EllipsisTooltip
            text={`${group.name} (${group.team_ids.length})`}
            className='teams-dropdown-lb-item-name'
          />
        </div>
      </div>
    );
  };

  const handleSetupClick = (team: T, e: React.MouseEvent): void => {
    e.stopPropagation();
    setSetupTeam(team);
    setDrawerOpen(true);
  };

  //Connected Team Item
  const renderTeamItem = (team: T): React.ReactNode => {
    const isSelected = selectedTeams.some((t) => t.id === team.id);

    return (
      <div
        key={team.id}
        className={`teams-dropdown-lb-item ${team.disabled ? 'is-disabled' : ''}`}
        onClick={() => {
          if (!team.disabled) {
            handleToggleSelect(team, !isSelected);
          }
        }}
      >
        <div className='teams-dropdown-lb-item-left'>
          <Checkbox
            className='checkbox-sm'
            checked={isSelected}
            disabled={team.disabled}
            onChange={(e) => {
              e.stopPropagation();
              handleToggleSelect(team, !isSelected);
            }}
          />
          <Typography.Text
            className='teams-dropdown-lb-item-dot'
            style={{
              backgroundColor: team.color || 'var(--gray-400)',
            }}
          />
          <EllipsisTooltip
            text={team.name}
            className='teams-dropdown-lb-item-name'
          />
        </div>
      </div>
    );
  };

  //Need setup team item
  const renderNeedSetupItem = (team: T): React.ReactNode => {
    return (
      <div key={team.id} className='teams-dropdown-lb-item'>
        <div className='teams-dropdown-lb-item-left'>
          <Typography.Text
            className='teams-dropdown-lb-item-dot'
            style={{
              backgroundColor: team.color || 'var(--gray-400)',
            }}
          />
          <EllipsisTooltip
            text={team.name}
            className='teams-dropdown-lb-item-name'
          />
        </div>
        <button
          type='button'
          className='teams-dropdown-lb-setup-btn'
          onClick={(e) => handleSetupClick(team, e)}
        >
          Set up
        </button>
      </div>
    );
  };

  const menu = (
    <Flex
      vertical
      className='teams-dropdown-lb-menu'
      style={{
        width: dropdownWidth ? `${dropdownWidth}px` : '100%',
      }}
      onClick={(e) => e.stopPropagation()}
    >
      {(filteredTeams?.length > 10 || Boolean(currentSearchQuery)) && (
        <div className='teams-dropdown-lb-search-wrapper'>
          <SearchInput
            placeholder={'Search recipients'}
            onChange={handleSearchChange}
            onClear={handleClearSearch}
            defaultValue={currentSearchQuery}
            width='100%'
          />
        </div>
      )}

      {pickableGroups.length > 0 && (
        <>
          <div className='teams-dropdown-lb-section'>
            <div className='teams-dropdown-lb-section-header'>
              {teamPickerSectionLabels().groups} · {pickableGroups.length}
            </div>
            {pickableGroups.map(renderGroupItem)}
          </div>
          {filteredTeams?.length > 0 && (
            <div className='teams-dropdown-lb-divider' />
          )}
        </>
      )}

      {!filteredTeams?.length && pickableGroups.length === 0 && (
        <div className='teams-dropdown-lb-empty'>
          {currentSearchQuery ? 'No matches found' : `No recipients available`}
        </div>
      )}
      {filteredTeams?.length > 0 && (
        <>
          {connectedTeams?.length > 0 && (
            <div className='teams-dropdown-lb-section'>
              <div className='teams-dropdown-lb-section-header'>
                Connected · {connectedTeams.length}
              </div>
              {connectedTeams.map(renderTeamItem)}
            </div>
          )}

          {connectedTeams?.length > 0 && needSetupTeams?.length > 0 && (
            <div className='teams-dropdown-lb-divider' />
          )}

          {needSetupTeams?.length > 0 && (
            <div className='teams-dropdown-lb-section'>
              <div className='teams-dropdown-lb-section-header'>
                Need setup · {needSetupTeams.length}
              </div>
              {needSetupTeams.map(renderNeedSetupItem)}
            </div>
          )}
        </>
      )}
    </Flex>
  );

  return (
    <div className='teams-dropdown-lb-container'>
      <Dropdown
        overlay={menu}
        trigger={['click']}
        onVisibleChange={handleVisibleChange}
        visible={open}
        disabled={disabled}
      >
        <div
          ref={triggerRef}
          onClick={toggleOpen}
          className={`teams-dropdown-lb-trigger ${disabled ? 'is-disabled' : ''} ${open ? 'is-open' : ''}`.trim()}
        >
          {selectedTeams?.length === 0 ? (
            <span className='teams-dropdown-lb-placeholder'>Select...</span>
          ) : (
            selectedTeams?.map((team) => (
              <TeamTagPill
                key={team.id}
                name={team.name}
                color={team.color}
                removable={!disabled}
                onRemove={(e) => handleRemove(team.id, e)}
              />
            ))
          )}

          <div className='teams-dropdown-lb-trigger-actions'>
            {selectedTeams?.length > 0 && !disabled && (
              <button
                type='button'
                className='teams-dropdown-lb-clear-btn'
                onClick={handleClearAll}
                title='Clear all'
              >
                <CloseIcon />
              </button>
            )}
            {!disabled && (
              <DoubleChevronDownRoleIcon
                className={`teams-dropdown-lb-arrow ${open ? 'is-open' : ''}`}
              />
            )}
          </div>
        </div>
      </Dropdown>

      <CommonDrawer
        open={drawerOpen}
        onClose={() => {
          setDrawerOpen(false);
          setSetupTeam(null);
        }}
        width={480}
      >
        {setupTeam && (
          <TeamForm
            teamData={{
              id: setupTeam.id,
              name: setupTeam.name,
              color: setupTeam.color || 'var(--gray-400)',
              general_support_email: setupTeam.general_support_email,
              slack_connected: setupTeam.slack_connected,
              teams_webhook: setupTeam.teams_webhook,
            }}
            onClose={() => {
              setDrawerOpen(false);
              setSetupTeam(null);
            }}
            refreshTeams={() => {
              const token = getToken();
              if (token) {
                dispatch(fetchTeamsDDL(token))
                  .unwrap()
                  .catch((): void => {});
              }
            }}
          />
        )}
      </CommonDrawer>
    </div>
  );
};

export default TeamsDropdownLB;
